import { useState } from "react";
import { Upload, FileText, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function UploadDialog({ subjects, defaultSubject, onCreated, trigger }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [subjectId, setSubjectId] = useState(defaultSubject || "");
  const [content, setContent] = useState("");
  const [files, setFiles] = useState([]);

  const reset = () => {
    setTitle(""); setContent(""); setFiles([]);
  };

  const resolveSubject = () => subjectId || defaultSubject || (subjects[0] && subjects[0].id);

  const submitText = async () => {
    const sid = resolveSubject();
    if (!title.trim() || !content.trim() || !sid) {
      toast.error("Inserisci titolo, materia e testo.");
      return;
    }
    setLoading(true);
    try {
      const mat = await api.createMaterial({ subject_id: sid, title: title.trim(), content: content.trim() });
      toast.success("Materiale salvato!");
      setOpen(false); reset();
      onCreated && onCreated(mat);
    } catch (e) {
      toast.error("Errore nel salvataggio.");
    } finally {
      setLoading(false);
    }
  };

  const submitFile = async () => {
    const sid = resolveSubject();
    if (!files.length || !sid) {
      toast.error("Seleziona almeno un file e una materia.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("subject_id", sid);
      fd.append("title", (title.trim() || files[0].name));
      let mat;
      if (files.length === 1) {
        fd.append("file", files[0]);
        mat = await api.uploadMaterial(fd);
      } else {
        files.forEach((f) => fd.append("files", f));
        mat = await api.uploadMaterialMulti(fd);
      }
      toast.success(files.length > 1 ? `${files.length} file uniti in un materiale!` : "File elaborato e testo estratto!");
      setOpen(false); reset();
      onCreated && onCreated(mat);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'estrazione del testo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setSubjectId(defaultSubject || ""); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="upload-material-button" className="rounded-xl gap-2">
            <Upload className="h-4 w-4" /> Carica Materiale
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Aggiungi Materiale di Studio</DialogTitle>
          <DialogDescription>Incolla del testo oppure carica un file (PDF, immagine, TXT). L'AI estrarrà il testo automaticamente.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label>Materia</Label>
          <Select value={subjectId} onValueChange={setSubjectId}>
            <SelectTrigger data-testid="upload-subject-select">
              <SelectValue placeholder="Scegli una materia" />
            </SelectTrigger>
            <SelectContent>
              {subjects.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Titolo</Label>
          <Input
            data-testid="upload-title-input"
            placeholder="Es. Capitolo 3 - La cellula"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <Tabs defaultValue="text" className="mt-1">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="text" data-testid="tab-paste-text">Incolla Testo</TabsTrigger>
            <TabsTrigger value="file" data-testid="tab-upload-file">Carica File</TabsTrigger>
          </TabsList>

          <TabsContent value="text" className="space-y-3">
            <Textarea
              data-testid="upload-content-textarea"
              placeholder="Incolla qui i tuoi appunti o il testo da studiare..."
              className="min-h-[160px]"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            <DialogFooter>
              <Button data-testid="submit-text-button" onClick={submitText} disabled={loading} className="rounded-xl gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Salva Materiale
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="file" className="space-y-3">
            <label
              htmlFor="file-input"
              className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl p-8 cursor-pointer hover:border-primary/50 hover:bg-accent/50 transition-colors"
            >
              <FileText className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground text-center">
                {files.length === 0
                  ? "PDF, immagini (foto appunti), TXT, CSV, MD"
                  : files.length === 1
                    ? files[0].name
                    : `${files.length} file selezionati`}
              </span>
              <input
                id="file-input"
                data-testid="upload-file-input"
                type="file"
                multiple
                accept=".pdf,.txt,.md,.csv,image/*"
                className="hidden"
                onChange={(e) => setFiles(Array.from(e.target.files))}
              />
            </label>
            {files.length > 1 && (
              <ul className="text-xs text-muted-foreground space-y-0.5 max-h-24 overflow-auto" data-testid="selected-files-list">
                {files.map((f, i) => <li key={i} className="truncate">• {f.name}</li>)}
              </ul>
            )}
            <p className="text-xs text-muted-foreground">Puoi caricare più foto insieme: verranno unite in un unico materiale.</p>
            <DialogFooter>
              <Button data-testid="submit-file-button" onClick={submitFile} disabled={loading} className="rounded-xl gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Estrai e Salva
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
